import { zodResolver } from '@hookform/resolvers/zod';
import type { ValidationErrorResponse } from '@wyz/types';
import {
  Button,
  Card,
  Col,
  Flex,
  FormContainer,
  FormFeedback,
  Icon,
  IconButton,
  Row,
  TabsFactory,
  Title,
} from '@wyz/ui';
import { SUPPLIERS_TYPES_IDS } from '@wyz/utils';
import { clsx } from 'clsx';
import { isNil } from 'lodash-es';
import * as React from 'react';
import {
  UseFormGetFieldState,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { Link } from 'react-router';
import { Trans, useI18n } from '~/common/context';
import { CommonErrorCodesEnumType, TranslationKeys } from '~/common/types';
import {
  AlphabeticalListSearchInput,
  BestSourcingConfigurationModal,
  SupplierAlphabeticalList,
  SupplierDraggables,
  type SupplierItem,
} from '~/connect/components';

import {
  CriteriaBuilder,
  MatchedCustomersTable,
  TextField,
} from '~/common/components';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { SupplierTemplateUtils } from '~/connect/helpers';
import { useScrollToNewItem } from '~/connect/hooks';
import {
  SUPPLIERS_STATUSES,
  supplierTemplateFormSchema,
} from '~/connect/schemas';
import {
  IAnonymizedSupplierPlatform,
  ISupplierTemplateForm,
  SupplierTemplateTabType,
} from '~/connect/types';
import { useManageBrands } from '~/common/api';
import { useBackTo, useValidationForm } from '~/common/hooks';
import { useManageCountries } from '~/connect/api';

// eslint-disable-next-line @wyz/module/no-relative-import
import SupplierDraggableItem from './supplier-draggable-item';

type SupplierTemplateFormProps =
  | {
      onSubmit: (
        formData: ISupplierTemplateForm,
        formState: {
          criteria: ReturnType<UseFormGetFieldState<ISupplierTemplateForm>>;
        },
      ) => void;
      isLoading: boolean;
      defaultValues?: ISupplierTemplateForm;
      errors: ValidationErrorResponse<CommonErrorCodesEnumType> | null;
      mode?: 'CREATE' | 'UPDATE';
      anonymizedSuppliers: IAnonymizedSupplierPlatform[];
    }
  | {
      mode?: 'READ_ONLY';
      defaultValues?: ISupplierTemplateForm;
      isLoading?: never;
      onSubmit?: never;
      errors?: never;
      anonymizedSuppliers: IAnonymizedSupplierPlatform[];
    };
const Tabs = TabsFactory<SupplierTemplateTabType>();

const DEFAULT_VALUES: ISupplierTemplateForm = {
  name: '',
  suppliers: [],
  criteria: [],
};

export const SupplierTemplateForm = ({
  anonymizedSuppliers,
  errors,
  defaultValues = DEFAULT_VALUES,
  isLoading,
  onSubmit,
  mode = 'CREATE',
}: SupplierTemplateFormProps) => {
  const backTo = useBackTo('/platform/distribution/supplier-templates');
  const { t } = useI18n();

  const manageBrands = useManageBrands({ list: { enabled: true } });

  const manageCountries = useManageCountries({ listActive: { enabled: true } });

  const methods = useForm<ISupplierTemplateForm>({
    resolver: zodResolver(supplierTemplateFormSchema),
    defaultValues: defaultValues,
  });
  const { remove, insert, fields, append } = useFieldArray<
    ISupplierTemplateForm,
    'suppliers',
    'key'
  >({
    control: methods.control,
    name: 'suppliers',
    keyName: 'key',
  });

  useValidationForm({
    methods,
    errors: errors?.errors,
    t,
  });
  const handleMove = React.useCallback(
    (from: number, to: number) => {
      const currentSuppliers = methods.getValues('suppliers');
      const value = currentSuppliers[from];
      remove(from);
      insert(to, {
        ...value,
        overridePriorityIfShorterDelivery:
          from === 0 && to !== 0
            ? true
            : value.overridePriorityIfShorterDelivery,
      });
    },
    [insert, methods, remove],
  );
  const handleOnAddSupplier = ({
    supplierPlatform,
  }: IAnonymizedSupplierPlatform) => {
    const isNewSupplier = isNil(
      fields.find(
        (supplier) => supplier.wyzApiId === supplierPlatform.supplier.id,
      ),
    );
    if (isNewSupplier) {
      const isBestSourcing =
        supplierPlatform.supplier.supplierType.id ===
        SUPPLIERS_TYPES_IDS.BEST_SOURCING;
      append({
        ...supplierPlatform.supplier,
        type: supplierPlatform.supplier.centralStock
          ? SUPPLIERS_TYPES_IDS.CENTRAL_STOCK
          : supplierPlatform.supplier.supplierType.id,
        status: isBestSourcing
          ? SUPPLIERS_STATUSES.ENABLED
          : SUPPLIERS_STATUSES.DISABLED,
        enabled: isBestSourcing,
        orderable: true,
        overridePriorityIfShorterDelivery: true,
        wyzApiId: supplierPlatform.supplier.id,
        childCustomerDelivery: false,
        bestSourcingConfigurations: isBestSourcing
          ? [
              {
                suppliers: [],
                brands: [],
              },
            ]
          : undefined,
      });
    }
  };
  const handleOnRemove = (id: number) => {
    const index = fields.findIndex((supplier) => supplier.wyzApiId === id);
    remove(index);
  };

  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [configuredSupplier, setConfiguredSupplier] = React.useState<
    SupplierItem | undefined
  >();
  const [bestSourcingConfigurationIndex, setBestSourcingConfigurationIndex] =
    React.useState<number | undefined>(undefined);
  const watchedSuppliers = useWatch({
    name: 'suppliers',
    control: methods.control,
  });
  const configuredSupplierIndex = configuredSupplier
    ? watchedSuppliers.findIndex(
        (field) => field.wyzApiId === configuredSupplier.wyzApiId,
      )
    : undefined;

  useScrollToNewItem(fields, 'supplier-name', 'wyzApiId');

  const bestSourcingConfigurations = watchedSuppliers.find(
    (supplier) => supplier.type === SUPPLIERS_TYPES_IDS.BEST_SOURCING,
  )?.bestSourcingConfigurations;
  const bestSourcingSupplierIds = isNil(bestSourcingConfigurations)
    ? []
    : Array.from(
        new Set(
          bestSourcingConfigurations.flatMap((configuration) =>
            configuration.suppliers.map((supplier) => supplier.value),
          ),
        ),
      );

  const groupedByLetter = SupplierTemplateUtils.groupSupplierByLetter(
    bestSourcingSupplierIds.length === 0
      ? anonymizedSuppliers
      : anonymizedSuppliers.filter(
          ({ supplierPlatform }) =>
            supplierPlatform.supplier.supplierType.id ===
              SUPPLIERS_TYPES_IDS.BEST_SOURCING ||
            !bestSourcingSupplierIds.includes(supplierPlatform.supplier.id),
        ),
    SupplierTemplateUtils.SUPPLIER_TEMPLATE_TABS,
  );

  return (
    <Row>
      <Col sm={12} md={12}>
        <FormContainer
          methods={methods}
          submitHandler={(formData) => {
            if (mode === 'CREATE' || !methods.formState.isDirty) {
              onSubmit?.(formData, {
                criteria: methods.getFieldState('criteria'),
              });
            } else if (mode === 'UPDATE') {
              onSubmit?.(formData, {
                criteria: methods.getFieldState('criteria'),
              });
            }
          }}
          formConfiguration={{
            mode: mode === 'READ_ONLY' ? 'READ_ONLY' : 'WRITE',
          }}
        >
          <Card className='mb-section'>
            <Card.Header>
              <Title heading='lg' color='tertiary' weight='bold'>
                <Icon icon='post' width='1.5rem' height='1.5rem' />
                <span className='ms-2'>
                  <Trans i18nKey='app.common.general_information' />
                </span>
              </Title>
            </Card.Header>
            <Card.Body>
              <Row>
                <TextField
                  required
                  name='name'
                  label={<Trans i18nKey='app.common.model_name' />}
                />
              </Row>
            </Card.Body>
          </Card>
          {/* Criteria Section */}
          <Card className='mb-section'>
            <Card.Header className='pb-0'>
              <Title heading='lg' color='tertiary' weight='bold'>
                <Icon icon='clip-board' width='1.5rem' height='1.5rem' />
                <span className='ms-2'>
                  <Trans i18nKey='app.common.criteria' />
                </span>
              </Title>
            </Card.Header>
            <Card.Body>
              <CriteriaBuilder>
                <Row className='mt-4'>
                  <Col md={6}>
                    <MatchedCustomersTable
                      featureContext={FEATURE_CONTEXT.SUPPLIER_TEMPLATE}
                      name={'criteria'}
                    />
                  </Col>
                </Row>
              </CriteriaBuilder>
            </Card.Body>
          </Card>

          {/* Suppliers Section  */}
          <Card className='mb-section'>
            <Card.Header className='pb-0'>
              <Title heading='lg' color='tertiary' weight='bold'>
                <Icon icon='team' width='1.5rem' height='1.5rem' />
                <span className='ms-2'>
                  <Trans i18nKey='app.supplier_template.form.suppliers.title' />
                </span>
              </Title>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col
                  md={configuredSupplier ? 3 : 7}
                  className={clsx('animate-slide', {
                    'opacity-25': configuredSupplier,
                  })}
                >
                  <AlphabeticalListSearchInput
                    value={searchQuery}
                    label={
                      <Trans i18nKey='app.supplier_template.form.suppliers.search_label' />
                    }
                    placeholder='Ex : BRIDGE W2S NISSAN FR, CHRONO LIMOGES,...'
                    onChange={setSearchQuery}
                  />
                  <Tabs.Container defaultActiveKey='ALL'>
                    <Tabs.List>
                      {SupplierTemplateUtils.SUPPLIER_TEMPLATE_TABS.map(
                        (tabItem) => (
                          <Tabs.Item
                            key={tabItem.value}
                            eventKey={tabItem.value}
                            id={tabItem.value}
                          >
                            <Trans i18nKey={tabItem.label} />
                          </Tabs.Item>
                        ),
                      )}
                    </Tabs.List>

                    <Tabs.Content>
                      {SupplierTemplateUtils.SUPPLIER_TEMPLATE_TABS.map(
                        (tabItem) => (
                          <Tabs.Pane
                            key={tabItem.value}
                            eventKey={tabItem.value}
                          >
                            <SupplierAlphabeticalList
                              currentTab={tabItem.groupFilterValue}
                              itemsByTab={
                                groupedByLetter[tabItem.groupFilterValue]
                              }
                              onAdd={handleOnAddSupplier}
                              onRemove={handleOnRemove}
                              searchQuery={searchQuery}
                            />
                          </Tabs.Pane>
                        ),
                      )}
                    </Tabs.Content>
                  </Tabs.Container>
                </Col>
                <Col
                  md={5}
                  className='position-relative animate-slide'
                  style={{ borderLeft: '1px solid var(--neutral-grey-4)' }}
                >
                  {configuredSupplier && (
                    <div
                      className='position-absolute'
                      style={{ right: '105%' }}
                    >
                      <IconButton
                        id='close-configuration'
                        icon='arrow-left-s-line'
                        variant='secondary'
                        onClick={() => setConfiguredSupplier(undefined)}
                        rounded
                      />
                    </div>
                  )}
                  <Title heading='lg' weight='bold'>
                    <Trans
                      i18nKey='app.supplier_template.form.suppliers.order_title'
                      values={{
                        count: fields.length,
                      }}
                    />
                  </Title>
                  <SupplierDraggables
                    featureContext={FEATURE_CONTEXT.SUPPLIER_TEMPLATE}
                    onMove={handleMove}
                    suppliers={watchedSuppliers}
                    onConfigure={(supplier) => {
                      setConfiguredSupplier(supplier);
                    }}
                    onRemove={handleOnRemove}
                    onConfigureBestSourcing={setBestSourcingConfigurationIndex}
                  />
                  {methods.formState.errors.suppliers?.message && (
                    <FormFeedback invalid id='suppliers.error'>
                      <Trans
                        i18nKey={
                          methods.formState.errors.suppliers
                            ?.message as keyof TranslationKeys
                        }
                      />
                    </FormFeedback>
                  )}
                </Col>
                {!isNil(configuredSupplier) &&
                  !isNil(configuredSupplierIndex) && (
                    <Col
                      key={configuredSupplierIndex}
                      md={4}
                      className='animate-slide'
                      style={{ borderLeft: '1px solid var(--neutral-grey-4)' }}
                    >
                      <Title heading='lg' weight='bold' wrap>
                        <Trans
                          i18nKey='app.supplier_template.form.suppliers.configure_supplier'
                          values={{
                            supplierName: configuredSupplier.name,
                          }}
                        />
                      </Title>
                      <SupplierDraggableItem
                        baseName={`suppliers.${configuredSupplierIndex}`}
                        supplier={{
                          id: configuredSupplier.wyzApiId,
                          type: configuredSupplier.type,
                        }}
                        index={configuredSupplierIndex}
                      />
                    </Col>
                  )}
              </Row>
            </Card.Body>
          </Card>

          {/* Action */}
          {mode !== 'READ_ONLY' && (
            <Card className='mt-3'>
              <Card.Body>
                <Flex className='space-x-4' justifyContent='between'>
                  <Button
                    id='cancel'
                    as={Link}
                    variant='secondary'
                    type='button'
                    to={backTo}
                  >
                    <Trans i18nKey='app.common.button.cancel' />
                  </Button>
                  <Button
                    id='submit'
                    variant='primary'
                    type='submit'
                    disabled={isLoading}
                  >
                    <Trans
                      i18nKey={
                        mode === 'CREATE'
                          ? 'app.supplier_template.form.create_submit_btn'
                          : 'app.supplier_template.form.edit_submit_btn'
                      }
                    />
                  </Button>
                </Flex>
              </Card.Body>
            </Card>
          )}
          {!isNil(bestSourcingConfigurationIndex) && (
            <BestSourcingConfigurationModal
              supplierIndex={bestSourcingConfigurationIndex}
              onClose={() => setBestSourcingConfigurationIndex(undefined)}
              brands={manageBrands.list.data?.data ?? []}
              suppliers={anonymizedSuppliers.filter(
                ({ supplierPlatform }) =>
                  supplierPlatform.supplier.supplierType.id !==
                    SUPPLIERS_TYPES_IDS.BEST_SOURCING &&
                  !watchedSuppliers.some(
                    (supplier) =>
                      supplier.wyzApiId === supplierPlatform.supplier.id,
                  ),
              )}
              countries={manageCountries.listActive.data?.data ?? []}
            />
          )}
        </FormContainer>
      </Col>
    </Row>
  );
};
